import React, { useState } from 'react';
import FormField from '../components/FormField';
import MyButton from '../components/MyButton';
import {
  validateInviteCode,
  registerParticipant,
  submitMountainData,
} from '../api/user';

/* ---------------- Reusable forms ---------------- */

const ParticipantForm = ({ title, token, onRegistered }) => {
  const [regData, setRegData] = useState({
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    birthdate: '',
    personal_email: '',
    nationality: '',
    personal_phone: '',
  });

  const [regResp, setRegResp] = useState(null);
  const [showMountain, setShowMountain] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const payload = { token, ...regData };
      const data = await registerParticipant(payload);
      setRegResp(data);
      onRegistered?.(data);
    } catch (err) {
      setRegResp({ error: err?.response?.data || err.message });
    }
  };

  return (
    <section style={{ marginBottom: 32 }}>
      <h3>{title}</h3>

      <form onSubmit={handleRegister}>
        {[
          ['username', 'Username'],
          ['password', 'Password', 'password'],
          ['first_name', 'First name'],
          ['last_name', 'Last name'],
          ['birthdate', 'Birthdate', 'date'],
          ['personal_email', 'Personal email', 'email'],
          ['nationality', 'Nationality'],
          ['personal_phone', 'Personal phone'],
        ].map(([name, label, type]) => (
          <FormField
            key={name}
            name={name}
            label={label}
            type={type || 'text'}
            value={regData[name]}
            onChange={(e) =>
              setRegData((p) => ({ ...p, [name]: e.target.value }))
            }
          />
        ))}

        <MyButton text="Register" type="submit" variant="primary" />
      </form>

      {regResp && (
        <pre style={{ background: '#f6f8fa', padding: 8 }}>
          {JSON.stringify(regResp, null, 2)}
        </pre>
      )}

      {regResp && !showMountain && (
        <MyButton
          text="Add mountain data (optional)"
          variant="secondary"
          onClick={() => setShowMountain(true)}
        />
      )}

      {showMountain && (
        <MountainForm token={token} />
      )}
    </section>
  );
};

const MountainForm = ({ token }) => {
  const [mountainData, setMountainData] = useState({
    participant_id: '',
    discipline: 'SKI',
    skill_level: 'BEGINNER',
    height_cm: '',
    weight_kg: '',
    shoe_size: '',
  });

  const [resp, setResp] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { token, ...mountainData };
      const data = await submitMountainData(payload);
      setResp(data);
    } catch (err) {
      setResp({ error: err?.response?.data || err.message });
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <h4>Mountain Data</h4>

      <form onSubmit={handleSubmit}>
        <FormField
          name="participant_id"
          label="Participant ID"
          value={mountainData.participant_id}
          onChange={(e) =>
            setMountainData((p) => ({
              ...p,
              participant_id: e.target.value,
            }))
          }
        />

        <div style={{ marginBottom: 12 }}>
          <label>Discipline</label>
          <select
            value={mountainData.discipline}
            onChange={(e) =>
              setMountainData((p) => ({ ...p, discipline: e.target.value }))
            }
          >
            <option value="SKI">SKI</option>
            <option value="SNOWBOARD">SNOWBOARD</option>
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label>Skill level</label>
          <select
            value={mountainData.skill_level}
            onChange={(e) =>
              setMountainData((p) => ({ ...p, skill_level: e.target.value }))
            }
          >
            <option value="BEGINNER">BEGINNER</option>
            <option value="INTERMEDIATE">INTERMEDIATE</option>
            <option value="PRO">PRO</option>
          </select>
        </div>

        {[
          ['height_cm', 'Height (cm)'],
          ['weight_kg', 'Weight (kg)'],
          ['shoe_size', 'Shoe size'],
        ].map(([name, label]) => (
          <FormField
            key={name}
            name={name}
            label={label}
            value={mountainData[name]}
            onChange={(e) =>
              setMountainData((p) => ({ ...p, [name]: e.target.value }))
            }
          />
        ))}

        <MyButton text="Submit Mountain Data" type="submit" />
      </form>

      {resp && (
        <pre style={{ background: '#f6f8fa', padding: 8 }}>
          {JSON.stringify(resp, null, 2)}
        </pre>
      )}
    </div>
  );
};

/* ---------------- Main tester ---------------- */

const ApiTester = () => {
  const [code, setCode] = useState('');
  const [token, setToken] = useState(null);
  const [inviteResp, setInviteResp] = useState(null);
  const [showInvitee, setShowInvitee] = useState(false);

  const handleValidate = async (e) => {
    e.preventDefault();
    try {
      const data = await validateInviteCode(code);
      setInviteResp(data);
      setToken(data.token);
    } catch (err) {
      setInviteResp({ error: err?.response?.data || err.message });
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 20 }}>
      <h2>API Tester</h2>

      <section style={{ marginBottom: 24 }}>
        <h3>1) Validate Invite Code</h3>
        <form onSubmit={handleValidate}>
          <FormField
            label="Invite Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <MyButton text="Validate" type="submit" />
        </form>

        {inviteResp && (
          <pre style={{ background: '#f6f8fa', padding: 8 }}>
            {JSON.stringify(inviteResp, null, 2)}
          </pre>
        )}
      </section>

      {token && (
        <>
          <ParticipantForm
            title="2) Main Participant"
            token={token}
            onRegistered={() => setShowInvitee(true)}
          />

          {showInvitee && (
            <ParticipantForm
              title="3) Invitee (optional)"
              token={token}
            />
          )}
        </>
      )}

      <div style={{ marginTop: 20 }}>
        <strong>Current token:</strong> {token || '—'}
      </div>
    </div>
  );
};

export default ApiTester;
